import { err, ok, type Result } from 'neverthrow';
import type { EdgeId, Graph, NodeId } from '../../types/graph';
import {
  CommandError,
  CommandErrorKind,
  CommandKind,
  CommandResult,
  GraphCommand,
  type CommandEffect,
} from './types';
import { FailCommand } from './elementary/fail';

/**
 * The CompositeCommand class allows the compound of elementary actions to be executed as a transaction,
 * and be represented as a single action. Thus allowing for more complex graph mutations but using a common base.
 */
export class CompositeCommand implements GraphCommand {
  readonly kind = CommandKind.Composite;

  constructor(
    public readonly children: GraphCommand[],
    public readonly label: string
  ) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    const inverses: GraphCommand[] = [];
    const dirtyNodes: Set<NodeId> = new Set();
    const dirtyEdges: Set<EdgeId> = new Set();
    let topologyChanged = false;

    const effects: CommandEffect[] = [];

    let nextGraph: Graph = graph as Graph;

    for (const child of this.children) {
      if (child instanceof FailCommand) {
        return err(child.error);
      }
    }

    for (const child of this.children) {
      const result = child.apply(nextGraph);
      if (result.isErr()) {
        let rollbackGraph: Graph = nextGraph;
        for (let i = inverses.length - 1; i >= 0; i -= 1) {
          const rollbackResult = inverses[i].apply(rollbackGraph);
          if (rollbackResult.isErr()) {
            return err({
              kind: CommandErrorKind.RollbackFailed,
              message: 'Failed to rollback composite command',
              details: {
                error: result.error,
                rollbackError: rollbackResult.error,
              },
            });
          }
          rollbackGraph = rollbackResult.value.graph;
        }
        return err(result.error);
      }

      nextGraph = result.value.graph;
      inverses.push(result.value.inverse);

      if (result.value.effects) {
        effects.push(...result.value.effects);
      }

      if (result.value.dirty) {
        // TODO: Do we want to check if the dirty nodes and edges still exist in the graph?
        const { nodes, edges, topologyChanged: isChanged } = result.value.dirty;
        nodes.forEach((n) => dirtyNodes.add(n));
        edges.forEach((e) => dirtyEdges.add(e));
        if (!topologyChanged) {
          topologyChanged = isChanged;
        }
      }
    }

    const inverse = new CompositeCommand(inverses.reverse(), this.label);

    return ok({
      graph: nextGraph,
      inverse,
      effects: effects.length > 0 ? effects : undefined,
      dirty: {
        nodes: Array.from(dirtyNodes),
        edges: Array.from(dirtyEdges),
        topologyChanged,
      },
    });
  }
}

/**
 * The DeferredCompositeCommand class is used when objects need to be constructed which require knowledge of objects in the graph.
 */
export class DeferredCompositeCommand implements GraphCommand {
  readonly kind = CommandKind.Composite;

  constructor(
    public readonly build: (graph: Readonly<Graph>) => GraphCommand[],
    public readonly label: string
  ) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    const children = this.build(graph);
    return new CompositeCommand(children, this.label).apply(graph);
  }
}
